import DocumentMeta from 'react-document-meta';
import authStyles from './../login/styles.module.css';
import inputStyles from './../login/input.module.css';
import React, { useState } from 'react';
import Header from '../header/index';
import { FaEye } from "react-icons/fa";
import ErrorMessage from '../../package/error/index';
import DotLoading from './../login/loading';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import { arrayUnion, collection, getDocs, getFirestore, limit, query, updateDoc, where } from 'firebase/firestore';
import Logo from './../login/items/logo';

// 審査員の新規登録ページ。src/salon/auth/login/0-signin.tsx（会社の新規登録）と同じ
// スタイル・Firebase初期化パターンを流用するが、新しくcompaniesドキュメントを作らない点が異なる。
// 事前に会社側の「応募者評価」設定タブで companies.judgeInviteEmails に登録されたメールアドレスでしか
// 登録できない（担当者による事前登録が実質的な認可ゲート）。
//
// この静的importはESモジュールの評価順の都合で src/index.js より先に実行され得るため、
// src/index.js と同じ条件分岐(E2E時はEmulator用の固定config)にして、
// どちらが先に initializeApp を呼んでも同一のconfigになるようにしている。
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';
const firebaseConfig = useEmulator
  ? {
      apiKey: 'demo-api-key',
      authDomain: 'localhost',
      projectId: 'demo-memorii-e2e',
      storageBucket: 'demo-memorii-e2e.appspot.com',
    }
  : {
      apiKey: process.env.REACT_APP_APIKEY,
      authDomain: process.env.REACT_APP_AUTHDOMAIN,
      databaseURL: process.env.REACT_APP_DATABASEURL,
      projectId: process.env.REACT_APP_PROJECT_ID,
      storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_APP_ID,
      measurementId: process.env.REACT_APP_MEASUREMENT_ID,
    };

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

type LoadingState = "idle" | "loading" | "success" | "error";

const JudgeSignIn: React.FC = () => {
  const auth = getAuth();
  const functions = getFunctions(app, "asia-northeast1");
  const navigate = useNavigate();

  const [ loading, setLoading ] = useState<LoadingState>("idle");
  const [ errorMessage, setErrorMessage ] = useState('');
  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ passwordEye, setPasswordEye ] = useState(false);

  const check = () => {
    setErrorMessage('');

    if (email.length <= 7) {
      setErrorMessage('メールアドレスを入力してください。');
    } else if (password.length <= 7) {
      setErrorMessage('パスワードを8文字以上入力してください。');
    } else {
      Register();
    }
  };

  const Register = async () => {
    setLoading("loading");

    const db = getFirestore();
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const inviteSnap = await getDocs(query(
        collection(db, 'companies'),
        where('judgeInviteEmails', 'array-contains', normalizedEmail),
        limit(1),
      ));

      if (inviteSnap.empty) {
        setLoading("error");
        setErrorMessage('このメールアドレスは審査員として登録されていません。担当者にご確認ください。');
        return;
      }

      const companyDoc = inviteSnap.docs[0];
      const companyData = companyDoc.data();
      const judgeInvites = Array.isArray(companyData.judgeInvites) ? companyData.judgeInvites : [];
      const invite = judgeInvites.find((j: any) => j.email === normalizedEmail);

      if (!invite) {
        setLoading("error");
        setErrorMessage('このメールアドレスは審査員として登録されていません。担当者にご確認ください。');
        return;
      }

      if (invite.uid) {
        setLoading("error");
        setErrorMessage('このメールアドレスは既に登録済みです。ログイン画面からログインしてください。');
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;

      // companies/{companyId} 内の招待に自分のuidを書き込んで招待を確定する
      await updateDoc(companyDoc.ref, {
        judgeUids: arrayUnion(user.uid),
        judgeInvites: judgeInvites.map((j: any) => (
          j.email === normalizedEmail ? { ...j, uid: user.uid, status: 'claimed' } : j
        )),
      });

      // アカウント作成・招待の確定は完了しているため、確認メールの送信に失敗しても
      // 登録全体を失敗扱いにはしない（/company/confirm 側に再送信ボタンがある）
      try {
        const sendVerificationEmail = httpsCallable(functions, "sendSignupVerificationEmail");
        await sendVerificationEmail({ email: user.email });
      } catch (mailError) {
        console.error('確認メールの送信に失敗しました:', mailError);
      }

      setLoading("success");
    } catch (error: any) {
      setLoading("error");
      switch (error.code) {
        case "auth/email-already-in-use":
          setErrorMessage("入力したメールアドレスは既に登録されています。");
          break;
        case "auth/invalid-email":
          setErrorMessage("入力したメールは無効なメールアドレス形式です。");
          break;
        case "auth/weak-password":
          setErrorMessage("パスワードが弱すぎます。より強力なパスワードを設定してください。");
          break;
        default:
          setErrorMessage("予期しないエラーが発生しました。時間をおいて再度お試しください。");
          break;
      }
    }
  };

  const meta = {
    title: "審査員登録 | 株式会社memorii（memorii works）",
    description: "株式会社memoriiが提供する『memorii works』の審査員アカウント登録ページです。",
    canonical: "https://memorii-works.com/company/judge-signin",
    meta: { charset: "utf-8", name: { robots: 'noindex,nofollow,noarchive,noimageindex' } }
  };

  const isWindows = navigator.userAgent.includes("Windows");
  const zoom = isWindows ? 0.8 : 1;

  return (
    <>
    <DocumentMeta {...meta}>
      <div className={authStyles.container}>

        <Header />

        <div className={authStyles.main} style={{ zoom }}>

          <div className={authStyles.auth}>

            {errorMessage ? <ErrorMessage message={errorMessage} /> : <div className={authStyles.errorDiv}></div>}

            {loading === "loading" && (
              <div className={authStyles.contents}>
                <DotLoading text="処理中" />
              </div>
            )}

            {(loading === "idle" || loading === "error") && (
              <div className={authStyles.contents}>
                <h4 className={authStyles.contents_title}>審査員登録</h4>

                <p className={inputStyles.input_title}>メールアドレス</p>
                <div className={inputStyles.input_input}>
                  <input placeholder='memorii@trankllc.com' value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                <p className={inputStyles.input_title}>パスワード</p>
                <div className={inputStyles.input_input}>
                  <input
                    placeholder='nk12q3ja'
                    type={passwordEye ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <FaEye className={inputStyles.input_icon} onClick={() => setPasswordEye(!passwordEye)} />
                </div>

                <button className={authStyles.contents_btn} onClick={() => check()}>登録する</button>

                <p className={authStyles.contents_link} onClick={() => navigate('/company/login')}>
                  ログイン画面に戻る
                </p>
              </div>
            )}

            {loading === "success" && (
              <div className={authStyles.contents}>
                <p>登録が完了しましたので、届いたメールのリンクをクリックし、本登録を完了してください。</p>

                <p className={authStyles.contents_link} onClick={() => navigate('/company/login')}>
                  ログインはこちらから
                </p>
              </div>
            )}

          </div>

          <div className={authStyles.line}></div>

          <div className={authStyles.logo_area}>
            <Logo />
          </div>

        </div>

      </div>
    </DocumentMeta>
    </>
  );
}

export default JudgeSignIn;
